import { Trans, useLingui } from "@lingui/react/macro";
import type { NavLink } from "@orbin/contracts";
import { ANDROID_APK_URL, IOS_APP_STORE_URL } from "@orbin/contracts";
import {
  AppMarkStack,
  Button,
  buttonVariants,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@orbin/ui-web";
import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Download,
  Info,
  Keyboard,
  LifeBuoy,
  MessageSquare,
  FolderOpen,
  Gauge,
  Link2,
  LogOut,
  Plug,
  ScrollText,
  Settings,
  Smartphone,
  SquareKanban,
  UserPlus,
  UserRound,
} from "lucide-react";
import type { ReactNode } from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { useAllowance } from "../lib/allowance";
import { useHostedService } from "../lib/hosted";
import { useSidebarUpdate } from "./DesktopUpdates";
import { IconTooltip } from "./IconTooltip";

// Preá Nuvem customers go to the public download and help pages, not the self-hosting page.
const CLOUD_MOBILE_APP_URL = "https://prea.ia.br/baixar/";
const CLOUD_DOCS_URL = "https://prea.ia.br/docs/";

function MenuButton({
  icon: Icon,
  label,
  onClick,
  children,
}: {
  icon: LucideIcon;
  label?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      variant="ghost"
      className="h-9 w-full justify-start gap-2.5 font-normal"
      aria-label={label}
      onClick={onClick}
    >
      <Icon className="text-muted-foreground" strokeWidth={1.75} />
      {children}
    </Button>
  );
}

type Props = {
  mini?: boolean;
  name: string;
  initials: string | null;
  image?: string | null;
  logos?: Array<{ name: string; logo?: string | null }>;
  email?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onIntegrations?: () => void;
  onSkills: () => void;
  onBoard?: () => void;
  links?: NavLink[];
  onLink?: (link: NavLink) => void;
  onArtifacts: () => void;
  onSettings: () => void;
  onUsage: () => void;
  onInvite?: () => void;
  onSupport?: () => void;
  onFeedback?: () => void;
  onShortcuts: () => void;
  onAbout: () => void;
  onLogOut: () => void;
  signingOut: boolean;
  logOutError: string | null;
};

// Marks are 24px wide, overlap by 2px, and need 2px for the outer ring.
export function sidebarMarkCount(availableWidth: number) {
  return Math.max(0, Math.min(3, Math.floor((availableWidth - 26) / 22) + 1));
}

export function SidebarFooter({
  mini = false,
  name,
  initials,
  image,
  logos = [],
  email,
  open,
  onOpenChange,
  onIntegrations,
  onSkills,
  onBoard,
  links = [],
  onLink,
  onArtifacts,
  onSettings,
  onUsage,
  onInvite,
  onSupport,
  onFeedback,
  onShortcuts,
  onAbout,
  onLogOut,
  signingOut,
  logOutError,
}: Props) {
  const { t } = useLingui();
  const update = useSidebarUpdate();
  const hosted = useHostedService();
  const allowance = useAllowance();
  const [view, setView] = useState<"main" | "support">("main");
  const mobileAppUrl = hosted ? CLOUD_MOBILE_APP_URL : IOS_APP_STORE_URL;
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const pillRef = useRef<HTMLButtonElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [markCount, setMarkCount] = useState(3);
  useLayoutEffect(() => {
    const pill = pillRef.current;
    const label = labelRef.current;
    if (!pill || !label || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const style = getComputedStyle(pill);
      const available =
        pill.clientWidth -
        Number.parseFloat(style.paddingLeft) -
        Number.parseFloat(style.paddingRight) -
        label.scrollWidth -
        Number.parseFloat(style.columnGap);
      setMarkCount(sidebarMarkCount(available));
    };
    const observer = new ResizeObserver(measure);
    observer.observe(pill);
    observer.observe(label);
    measure();
    return () => observer.disconnect();
  }, [mini]);
  return (
    <div
      data-testid="sidebar-footer"
      className={`flex shrink-0 items-center gap-2 bg-transparent ${mini ? "flex-col px-1 py-2" : "px-2.5 py-3"}`}
    >
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (!next) setView("main");
          onOpenChange(next);
        }}
      >
        <PopoverTrigger
          data-testid="user-menu-trigger"
          aria-label={t`Account menu for ${name}`}
          className="grid size-9 shrink-0 place-items-center overflow-hidden rounded-full border border-border bg-sidebar-accent text-sm font-medium text-foreground outline-none hover:border-foreground/30 focus-visible:ring-2 focus-visible:ring-ring"
        >
          {image && failedImage !== image ? (
            <img
              src={image}
              alt=""
              className="size-full object-cover"
              onError={() => setFailedImage(image)}
            />
          ) : initials ? (
            initials
          ) : (
            <UserRound size={18} aria-hidden="true" />
          )}
        </PopoverTrigger>
        {open ? (
          <PopoverContent
            side="top"
            sideOffset={8}
            align="start"
            className="max-h-(--available-height) w-[295px] max-w-[calc(100vw-69px)] gap-0 overflow-y-auto p-1 *:shrink-0 data-closed:animate-none"
          >
            <div
              data-testid="account-menu-header"
              className="mb-1 min-w-0 border-b border-border px-2.5 py-2.5"
            >
              <p className="truncate text-sm font-medium">{name}</p>
              {email && email !== name ? (
                <p className="truncate text-xs text-muted-foreground">{email}</p>
              ) : null}
            </div>
            {view === "support" ? (
              <>
                <Button
                  variant="ghost"
                  className="h-9 w-full justify-start gap-2.5 font-normal"
                  onClick={() => setView("main")}
                >
                  <ChevronLeft className="text-muted-foreground" strokeWidth={1.75} />
                  <Trans>Support</Trans>
                </Button>
                <div className="my-1 border-t border-border" />
                <a
                  className={`${buttonVariants({ variant: "ghost" })} h-9 w-full justify-start gap-2.5 font-normal`}
                  href={hosted ? CLOUD_DOCS_URL : "https://prea.ia.br/instalavel/"}
                  target="_blank"
                  rel="noreferrer"
                >
                  <BookOpen className="text-muted-foreground" strokeWidth={1.75} />
                  <Trans>Help center</Trans>
                </a>
                {onSupport ? (
                  <MenuButton icon={LifeBuoy} onClick={onSupport}>
                    <Trans>Talk to support</Trans>
                  </MenuButton>
                ) : null}
                {onFeedback ? (
                  <MenuButton icon={MessageSquare} onClick={onFeedback}>
                    <Trans>Send feedback</Trans>
                  </MenuButton>
                ) : null}
                <MenuButton icon={Keyboard} onClick={onShortcuts}>
                  <Trans>Keyboard shortcuts</Trans>
                </MenuButton>
                <MenuButton icon={Info} onClick={onAbout}>
                  <Trans>About</Trans>
                </MenuButton>
              </>
            ) : (
              <>
                {allowance ? (
                  <button
                    type="button"
                    data-testid="account-menu-plan"
                    data-level={allowance.level}
                    onClick={onUsage}
                    className="flex h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-start text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Gauge className="size-4 text-muted-foreground" strokeWidth={1.75} />
                    <span className="min-w-0 flex-1 truncate">{allowance.planName ?? t`Plan`}</span>
                    <span
                      className={`tabular-nums ${
                        allowance.level === "full"
                          ? "text-destructive"
                          : allowance.level === "warn"
                            ? "text-warning"
                            : "text-muted-foreground"
                      }`}
                    >
                      {t`${allowance.percent}% used`}
                    </span>
                    <ChevronRight className="size-4 text-muted-foreground" strokeWidth={1.75} />
                  </button>
                ) : (
                  <MenuButton icon={Gauge} onClick={onUsage}>
                    <Trans>Usage</Trans>
                  </MenuButton>
                )}
                {mini && onIntegrations ? (
                  <MenuButton icon={Plug} onClick={onIntegrations}>
                    <Trans>Connect apps</Trans>
                  </MenuButton>
                ) : null}
                <MenuButton icon={ScrollText} onClick={onSkills}>
                  <Trans>Skills</Trans>
                </MenuButton>
                <MenuButton icon={FolderOpen} onClick={onArtifacts}>
                  <Trans>Artifacts</Trans>
                </MenuButton>
                {onBoard ? (
                  <MenuButton icon={SquareKanban} onClick={onBoard}>
                    <Trans>Board</Trans>
                  </MenuButton>
                ) : null}
                {links.map((link) => (
                  <MenuButton key={link.id} icon={Link2} onClick={() => onLink?.(link)}>
                    {link.label}
                  </MenuButton>
                ))}
                <div className="my-1 border-t border-border" />
                <a
                  className={`${buttonVariants({ variant: "ghost" })} h-9 w-full justify-start gap-2.5 font-normal`}
                  href={/Android/i.test(navigator.userAgent) ? ANDROID_APK_URL : mobileAppUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Smartphone className="text-muted-foreground" strokeWidth={1.75} />
                  <Trans>Get the mobile app</Trans>
                </a>
                {onInvite ? (
                  <MenuButton icon={UserPlus} onClick={onInvite}>
                    <Trans>Invite friends</Trans>
                  </MenuButton>
                ) : null}
                <Button
                  variant="ghost"
                  className="h-9 w-full justify-start gap-2.5 font-normal"
                  onClick={() => setView("support")}
                >
                  <LifeBuoy className="text-muted-foreground" strokeWidth={1.75} />
                  <span className="flex-1 text-start">
                    <Trans>Support</Trans>
                  </span>
                  <ChevronRight className="text-muted-foreground" strokeWidth={1.75} />
                </Button>
                <MenuButton icon={Settings} label={t`Settings`} onClick={onSettings}>
                  <Trans>Settings</Trans>
                </MenuButton>
                <div className="my-1 border-t border-border" />
                <Button
                  variant="ghost"
                  className="h-9 w-full justify-start gap-2.5 font-normal"
                  disabled={signingOut}
                  onClick={onLogOut}
                >
                  <LogOut className="text-muted-foreground" strokeWidth={1.75} />
                  <Trans>Log out</Trans>
                </Button>
              </>
            )}
            {logOutError ? (
              <p role="alert" className="px-2.5 pt-1 pb-2 text-body2 text-destructive">
                {logOutError}
              </p>
            ) : null}
          </PopoverContent>
        ) : null}
      </Popover>
      {!mini && onIntegrations ? (
        <Button
          variant="outline"
          ref={pillRef}
          onClick={onIntegrations}
          className="h-9 min-w-0 flex-1 justify-between bg-sidebar-accent hover:border-foreground/30 hover:bg-sidebar-accent dark:hover:bg-sidebar-accent"
        >
          <span ref={labelRef} className="min-w-0 truncate">
            <Trans>Connect apps</Trans>
          </span>
          <AppMarkStack marks={logos.slice(0, markCount)} ringClassName="ring-sidebar-accent" />
        </Button>
      ) : null}
      {update.available ? (
        <IconTooltip label={t`Install update`}>
          <Button
            size="icon-lg"
            className="rounded-full"
            aria-label={t`Install update`}
            disabled={update.busy}
            onClick={update.install}
          >
            <Download aria-hidden="true" />
          </Button>
        </IconTooltip>
      ) : null}
    </div>
  );
}
